import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

import { CardWrapper, ErrorBoundary, FormField, useHookForm } from '@monorepo/common';
import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { convertingApplicationEmail } from './funcConvertingApplicationEmail';
import AdditionalEmail from '../../../../components/JobStepper/components/AdditionalEmail';
import { EOptions, step5Options } from './step5Options';
import { IEventsStepProps } from '../../interfaces';
import { IStep5Form } from './formInterfaces';
import { schema } from './schema';
import { useCommitOnLeave } from '../useCommitOnLeave';
import ContactApplicationTitle from '../../../../components/JobStepper/components/ContactApplicationTitle';
import JobDeliveryMethodSelector from '../../../../components/JobStepper/components/JobDeliveryMethodSelector';

interface IStep5Props extends IEventsStepProps {
  setStep5: (value: IStep5Form) => void;
  defaultValue: IStep5Form;
  onSave?: (value: IStep5Form) => void;
  onDraftChange?: (value: IStep5Form) => void;
  registerCommit?: (commitNow: () => void) => () => void;
}

export const Step5 = memo(({
  Nav,
  onNext,
  onBack,
  onSave,
  setStep5,
  defaultValue,
  onDraftChange,
  registerCommit,
}:IStep5Props) => {
  const submitIntent = useRef<'next' | 'save'>('next');
  const [isShowUrl, setIsShowUrl] = useState(false);
  const [changedFields, setChangedFields] = useState<IStep5Form | null>(null);

  const {
    useFieldArray,
    setValue,
    register,
    handleSubmit,
    getValues,
    control,
    errors
  } = useHookForm<IStep5Form>({
    schema,
    defaultValues: defaultValue,
    showChangedFields: (a) => {
      setChangedFields(a);
      onDraftChange?.(a);
    }
  });

  const markCommitted = useCommitOnLeave(() => {
    const values = getValues();
    if (JSON.stringify(values) !== JSON.stringify(defaultValue)) {
      setStep5({
        ...values,
        applicationEmail: convertingApplicationEmail(values.tempAdditionalEmails || []),
        applicationUrlEnabled: !!isShowUrl
      });
    }
  }, registerCommit);

  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultValue).toString() !== JSON.stringify(changedFields))) {
        setStep5({
          ...changedFields,
          applicationEmail: convertingApplicationEmail(changedFields.tempAdditionalEmails),
          applicationUrlEnabled: !!isShowUrl
        });
      }
      markCommitted();
      onBack();
    }
  };

  const { fields, append, remove } = useFieldArray({
    name: 'tempAdditionalEmails',
    control
  });

  const increase = useCallback(() => {
    append({
      email: null
    });
  }, [append]);

  const decrease = (id: number) => {
    if (fields.length !== 0) {
      remove(id);
    }
  };

  const onSubmit = (data: IStep5Form) => {
    if (data) {
      data.applicationEmail = convertingApplicationEmail(data.tempAdditionalEmails);
      data.applicationUrlEnabled = data.applicationUrlEnabled !== '0';
      setStep5(data);
      if (submitIntent.current === 'save') {
        onSave?.(data);
      } else {
        markCommitted();
        onNext?.();
      }
      submitIntent.current = 'next';
    }
  };

  const handleDeliveryMethodChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event && !Array.isArray(event)) {
      setValue('applicationUrlEnabled', event.target.value);
      setValue('applicationUrl', null);
      if (event.target.value === EOptions.APPLICATIONS) {
        increase();
        setIsShowUrl(false);
        setValue('applicationUrl', null);
        setValue('showEmails', true);
        return;
      }
      setValue('tempAdditionalEmails', []);
      setValue('showEmails', false);
      setIsShowUrl(true);
    }
  };

  useEffect(() => {
    Object.keys(defaultValue).forEach((item) => {
      if (item !== 'tempAdditionalEmails' && item !== 'applicationURLEnabled') {
        // @ts-ignore
        setValue(item, defaultValue[item]);
      }
    });
    if (!defaultValue.applicationUrlEnabled) {
      setValue('applicationUrlEnabled', step5Options[0].value);
    }
    if (defaultValue.applicationUrlEnabled) {
      setValue('applicationUrlEnabled', step5Options[1].value);
    }

    setIsShowUrl(defaultValue.applicationUrlEnabled as boolean);

    setValue('tempAdditionalEmails', []);
    let emailStr = '';
    if (defaultValue?.tempAdditionalEmails?.length > 0) {
      defaultValue.tempAdditionalEmails.forEach((item) => {
        if (item.email) {
          append(item);
          emailStr += `${item.email}; `;
        }
      });
    }
    if (!defaultValue.applicationUrlEnabled && defaultValue.applicationEmail?.length === 0) {
      append({
        email: null
      });
    }
    if (defaultValue?.showEmails === null) {
      setValue('showEmails', false);
    }

    setValue('applicationEmail', emailStr);
  }, [defaultValue]);

  return (
    <CardWrapper
      dataCy="step5"
      scrollOptions={{
        isErrorsAutoScrollPage: errors
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <input
          style={{ display: 'none' }}
          type="checkbox"
          {...register('showEmails')}
        />
        <input
          type="hidden"
          {...register('applicationEmail')}
        />

        <ContactApplicationTitle />

        <Grid
          container
          columnSpacing={2}
          rowSpacing={1}
        >
          <Grid
            item
            xs={12}
            sm={6}
          >
            <FormField
              isDisabled
              isRequired
              extraProps={{ ...register('contactFirstName') }}
              errors={errors}
              label="Your First Name"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            <FormField
              isDisabled
              isRequired
              extraProps={{ ...register('contactLastName') }}
              errors={errors}
              label="Your Last Name"
            />
          </Grid>
        </Grid>
        <FormField
          isDisabled
          isRequired
          extraProps={{ ...register('email') }}
          errors={errors}
          label="Your Email"
        />

        <Typography
          variant="h6"
          sx={{
            mb: 2
          }}
        >
          Job Application Delivery Method
        </Typography>

        <JobDeliveryMethodSelector
          options={step5Options}
          value={(getValues('applicationUrlEnabled') || step5Options[0].value).toString()}
          onChange={handleDeliveryMethodChange}
        />

        {!isShowUrl && (
        <Typography
          variant="body2"
          sx={{
            mb: 2
          }}
        >
          Applications for this job will be sent to:
        </Typography>
        )}

        <ErrorBoundary>
          {!isShowUrl && fields && fields.length ? fields.map((item, index) => (
            <AdditionalEmail
              generalFieldsLength={fields.length}
              id={index}
              register={register}
              errors={{
                ...(errors?.tempAdditionalEmails) && ({ ...errors.tempAdditionalEmails[index] })
              }}
              decrease={decrease}
              key={item.id}
              nameField={`tempAdditionalEmails.${index}.email` as const}
            />
          )) : null}
        </ErrorBoundary>

        <FormField
          isRequired
          extraProps={{ ...register('applicationUrl') }}
          placeholder="Url"
          visible={isShowUrl}
          errors={errors}
          label="URL"
        />

        {!isShowUrl && (
        <Button
          type="button"
          variant="outlined"
          color="info"
          startIcon={<Add />}
          onClick={() => {
            increase();
          }}
          sx={{ mt: 2 }}
        >
          Add additional email
        </Button>
        )}

        <Nav
          nextDisabled={false}
          onBack={goBack}
          nextButton={(
            <Stack
              className="cpg-edit-step-actions"
              direction={{ xs: 'column', sm: 'row' }}
            >
              <Button
                startIcon={<SaveIcon />}
                variant="outlined"
                type="submit"
                onClick={() => {
                  submitIntent.current = 'save';
                }}
                data-cy="saveListing"
              >
                Save Listing
              </Button>
              <Button
                endIcon={(<ArrowRightIcon fontSize="small" />)}
                variant="contained"
                data-cy="nextStep"
                type="submit"
                onClick={() => {
                  submitIntent.current = 'next';
                }}
              >
                Next Step
              </Button>
            </Stack>
          )}
        />
      </form>
    </CardWrapper>
  );
});
